
import React, { useState, useEffect, useCallback } from 'react';
import { X, Search, MapPin, Send, Loader2, Navigation } from 'lucide-react';

const AddressInputModal = ({ onClose, onSendAddress }) => {
    const [address, setAddress] = useState('');
    const [suggestions, setSuggestions] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const fetchSuggestions = useCallback(async (text) => {
        if (text.length < 4) {
            setSuggestions([]);
            return;
        }
        setIsLoading(true);
        setError('');
        try {
            // Busca endereços no Brasil para maior precisão
            const response = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(text)}&format=json&limit=6&addressdetails=1&countrycodes=br`);
            const data = await response.json();
            setSuggestions(data || []);
        } catch (e) {
            console.error("Erro na busca:", e);
        }
        setIsLoading(false);
    }, []);

    useEffect(() => {
        const handler = setTimeout(() => {
            if (address.length >= 4) fetchSuggestions(address);
        }, 600);
        return () => clearTimeout(handler);
    }, [address, fetchSuggestions]);

    const handleSelectSuggestion = (suggestion) => {
        setAddress(suggestion.display_name);
        setSuggestions([]);
        // Já envia as coordenadas diretamente após selecionar para agilizar o fluxo
        onSendAddress({ latitude: parseFloat(suggestion.lat), longitude: parseFloat(suggestion.lon) });
    };

    return (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[100] flex items-center justify-center p-4 transition-all" onClick={onClose}>
            <div
                className="bg-gray-50 rounded-[2.5rem] w-full max-w-md relative animate-slide-up flex flex-col overflow-hidden shadow-2xl"
                style={{ maxHeight: '85vh' }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Cabeçalho */}
                <header className="p-5 bg-white border-b flex justify-between items-center shrink-0">
                    <div>
                        <h2 className="text-xl font-black text-gray-800 leading-tight uppercase tracking-tighter">Inserir Local</h2>
                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Busque um endereço ou ponto de referência</p>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors bg-gray-50 border border-gray-100">
                        <X size={20} className="text-gray-500" />
                    </button>
                </header>

                <main className="p-5 flex-grow flex flex-col min-h-0">
                    {/* Campo de Pesquisa */}
                    <div className="relative mb-4 shrink-0">
                        <Search className={`absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 transition-colors ${isLoading ? 'text-blue-500' : 'text-gray-400'}`} />
                        <input
                            type="text"
                            autoFocus
                            value={address}
                            onChange={(e) => setAddress(e.target.value)}
                            placeholder="Ex: Rua das Flores, 123..."
                            className="w-full p-4 pl-12 border-2 border-transparent rounded-[1.5rem] bg-white shadow-sm focus:border-blue-500 focus:bg-white outline-none transition-all text-gray-800 font-medium"
                        />
                        {isLoading && (
                            <div className="absolute right-4 top-1/2 -translate-y-1/2">
                                <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                            </div>
                        )}
                    </div>

                    {/* Lista de Sugestões */}
                    <div className="flex-grow overflow-y-auto pr-1">
                        {suggestions.length > 0 ? (
                            <div className="space-y-2 pb-4">
                                {suggestions.map((s) => (
                                    <button
                                        key={s.place_id}
                                        onClick={() => handleSelectSuggestion(s)}
                                        className="w-full text-left p-4 bg-white hover:bg-blue-50 border border-gray-100 rounded-2xl transition-all flex items-start group shadow-sm active:scale-[0.98]"
                                    >
                                        <div className="bg-blue-50 p-2 rounded-xl mr-4 group-hover:bg-blue-100 transition-colors flex-shrink-0">
                                            <MapPin size={18} className="text-blue-600" />
                                        </div>
                                        <div className="min-w-0">
                                            <p className="font-bold text-gray-800 text-sm leading-tight mb-1 truncate">
                                                {s.display_name.split(',')[0]}
                                            </p>
                                            <p className="text-[11px] text-gray-500 leading-snug">
                                                {s.display_name.split(',').slice(1).join(',').trim()}
                                            </p>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        ) : address.length >= 4 && !isLoading ? (
                            <div className="text-center py-10 opacity-50 flex flex-col items-center">
                                <Search size={40} className="text-gray-300 mb-2" />
                                <p className="text-sm font-bold text-gray-400 uppercase tracking-widest">Nenhum local encontrado</p>
                            </div>
                        ) : (
                            <div className="text-center py-10 opacity-30 flex flex-col items-center">
                                <Navigation size={40} className="text-gray-300 mb-2" />
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-widest leading-relaxed">
                                    Digite o endereço completo<br/>para uma busca mais precisa
                                </p>
                            </div>
                        )}
                    </div>
                </main>

                {/* Rodapé informativo sutil */}
                <footer className="p-4 bg-gray-100/50 border-t flex justify-center shrink-0">
                    <p className="text-[9px] font-black text-gray-400 uppercase tracking-tighter flex items-center">
                        <MapPin size={10} className="mr-1" /> Tecnologia de Geocodificação OpenStreetMap
                    </p>
                </footer>
            </div>
        </div>
    );
};

export default AddressInputModal;
